import { useCallback, useEffect, useState } from 'react';
import { ArtistPortalConnectFailure, ArtistPortalConnectResult } from 'fym-shared/src/smartlinks/artistPortals';
import { ServiceConnection } from '../../../redux/api/fymAPI.types';
import { usePortalConnectReturn } from './usePortalConnectReturn';

interface UseCatalogPortalReturnProps {
  connections: ServiceConnection[] | undefined;
  /** Reloads the cards, so they show what the sign-in changed. */
  reload: () => void;
  /** Opens the connect sheet on one card. */
  openConnect: (connection: ServiceConnection) => void;
  /** Shows the cards after a sign-in that connected. They sit below the catalog. */
  onConnected: () => void;
}

/**
 * What the Catalog screen does when a YouTube sign-in sends the browser back.
 *
 * `connected` scrolls to the cards, which sit below the catalog, so the artist
 * sees the card show the channel. `pickArtist`
 * opens the connect sheet, which asks for the channel list and shows the
 * picker. `error` opens the sheet with the reason, so the artist can try again
 * from the same place.
 *
 * It waits for the cards to load, because the sheet needs the card's row.
 */
export function useCatalogPortalReturn({ connections, reload, openConnect, onConnected }: UseCatalogPortalReturnProps) {
  const { outcome, dismiss } = usePortalConnectReturn({ onReturn: reload });
  const [callbackFailure, setCallbackFailure] = useState<ArtistPortalConnectFailure>();

  useEffect(() => {
    if (outcome === undefined) {
      return;
    }
    const connection = connections?.find((one) => one.integrationType === outcome.integrationType);
    if (connection === undefined) {
      return;
    }
    dismiss();
    if (outcome.result === ArtistPortalConnectResult.connected) {
      onConnected();
      return;
    }
    setCallbackFailure(outcome.reason);
    openConnect(connection);
  }, [connections, dismiss, onConnected, openConnect, outcome]);

  const clearCallbackFailure = useCallback(() => setCallbackFailure(undefined), []);

  return { callbackFailure, clearCallbackFailure };
}
